import React, { useLayoutEffect, useRef, useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { useTranslation } from "react-i18next";

import { atQuery, findMentions, insertMention, matchPeople } from "./nlqModel";

const MENU_WIDTH = 320;

export const ALL_FOLDERS = "";
// longest query the API takes (POST /search/nlq answers 400 over it)
export const MAX_QUERY = 500;

// "Understood as" chip text and tooltip of a concept {kind, value}
export function conceptLabel(t, c) {
  switch (c.kind) {
    case "speaker":
      return [t("{{name}} speaking", { name: c.value }), t("Must be the one speaking")];
    case "character":
      return [t("{{name}} on screen", { name: c.value }), t("Only needs to be on screen")];
    case "phrase":
      return [t("said exactly “{{value}}”", { value: c.value }), t("These exact words are said")];
    default:
      return [t("about “{{value}}”", { value: c.value }), t("Searched as a whole in what’s said and what happens")];
  }
}

const initials = (name) =>
  name
    .split(/\s+/)
    .filter((w) => /^[\p{L}\p{N}]/u.test(w))
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// One-line search: the query with @mentions of people from the folder, the folder to search in, and what the query
// is understood as
// estimate: estimated seconds of the search, shown instead of what the query is understood as (empty query or
// searching)
export default function QueryBar({ text, onText, onSubmit, people, folder, folders, onFolder, concepts, dirty, estimate, busy }) {
  const { t } = useTranslation();
  const input = useRef(null);
  const mirror = useRef(null);
  const atMark = useRef(null);
  const [caret, setCaret] = useState(null);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState(null); // position of the @ whose list was closed with Esc
  const [menuLeft, setMenuLeft] = useState(0);
  const [folderMenu, setFolderMenu] = useState(null);
  const pendingCaret = useRef(null); // caret to restore once the inserted mention is rendered

  const folderName = folder === ALL_FOLDERS ? t("all folders") : folder;
  const names = people.map((p) => p.name);
  const at = focused ? atQuery(text, caret === null ? text.length : caret) : null;
  let items = [];
  let open = false;
  if (at && at.start !== dismissed) {
    items = matchPeople(people, at.q);
    open = items.length > 0 || !/\s/.test(at.q);
  }
  const ai = Math.min(active, Math.max(items.length - 1, 0));

  // keep the highlighted text under the transparent input, and the list under the @ being typed
  const sync = () => {
    if (mirror.current && input.current) mirror.current.scrollLeft = input.current.scrollLeft;
  };
  useLayoutEffect(() => {
    if (pendingCaret.current !== null && input.current) {
      input.current.focus();
      input.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
    sync();
    if (open && atMark.current && input.current) {
      const left = atMark.current.offsetLeft - input.current.scrollLeft;
      setMenuLeft(Math.max(0, Math.min(left, input.current.offsetWidth - MENU_WIDTH)));
    }
  }, [open, text, caret]);

  const updateCaret = (e) => setCaret(e.target.selectionStart);

  const change = (e) => {
    const el = e.target;
    if (dismissed !== null && el.value[dismissed] !== "@") setDismissed(null);
    setActive(0);
    setCaret(el.selectionStart);
    onText(el.value);
  };

  const pick = (name) => {
    const next = insertMention(text, at, name);
    pendingCaret.current = next.caret;
    onText(next.text);
    setCaret(next.caret);
    setActive(0);
  };

  const keyDown = (e) => {
    if (open && items.length && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setActive((ai + (e.key === "ArrowDown" ? 1 : items.length - 1)) % items.length);
    } else if (open && items.length && (e.key === "Enter" || e.key === "Tab")) {
      e.preventDefault();
      pick(items[ai].name);
    } else if (open && e.key === "Escape") {
      e.preventDefault();
      setDismissed(at.start);
    } else if (e.key === "Enter") {
      e.preventDefault();
      onSubmit();
    }
  };

  // mirror segments: mentions as pills, the @ being typed in the accent color
  const segments = [];
  const spans = findMentions(text, names).map((m) => [m.start, m.end, "mention"]);
  if (open && !spans.some(([a, b]) => at.start >= a && at.start < b)) spans.push([at.start, at.end, "typing"]);
  spans.sort((a, b) => a[0] - b[0]);
  let pos = 0;
  spans.forEach(([a, b, kind]) => {
    if (a < pos) return;
    if (a > pos) segments.push(<span key={pos}>{text.slice(pos, a)}</span>);
    segments.push(
      <span key={a} className={"qb-" + kind} ref={kind === "typing" ? atMark : undefined}>
        {text.slice(a, b)}
      </span>,
    );
    pos = b;
  });
  if (pos < text.length) segments.push(<span key={pos}>{text.slice(pos)}</span>);

  const lo = at ? at.q.length : 0;
  return (
    <div className="qb">
      <div className="qb-row">
        <div className="qb-field qb-query">
          <label className="qb-label" htmlFor="qb-input">
            {t("Search for")}
          </label>
          <div className="qb-text">
            <div ref={mirror} className="qb-mirror" aria-hidden="true">
              {text ? segments : <span className="qb-placeholder">{t("Describe the moment · @ to mention someone · \"quotes\" for exact words")}</span>}
            </div>
            <input
              id="qb-input"
              ref={input}
              className="qb-input"
              value={text}
              onChange={change}
              onKeyDown={keyDown}
              onSelect={updateCaret}
              onClick={updateCaret}
              onKeyUp={sync}
              onScroll={sync}
              onFocus={(e) => {
                setFocused(true);
                updateCaret(e);
              }}
              onBlur={() => setFocused(false)}
              spellCheck="false"
              autoComplete="off"
              maxLength={MAX_QUERY}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls="qb-people"
              aria-activedescendant={open && items.length ? "qb-person-" + ai : undefined}
            />
          </div>
          {text && (
            <button
              type="button"
              className="qb-clear"
              title={t("Clear")}
              aria-label={t("Clear")}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onText("");
                setCaret(0);
                input.current?.focus();
              }}
            >
              <CloseIcon fontSize="inherit" />
            </button>
          )}
          {open && (
            <div className="qb-people" style={{ left: menuLeft }} onMouseDown={(e) => e.preventDefault()}>
              <div className="qb-people-head">
                <FolderIcon fontSize="inherit" />
                <span>{t("People in {{folder}}", { folder: folderName })}</span>
              </div>
              <div id="qb-people" role="listbox" aria-label={t("People in {{folder}}", { folder: folderName })}>
                {items.map((p, i) => (
                  <div
                    key={p.name}
                    id={"qb-person-" + i}
                    role="option"
                    aria-selected={i === ai}
                    className="qb-person"
                    onClick={() => pick(p.name)}
                    onMouseEnter={() => setActive(i)}
                  >
                    <span className="qb-avatar">{initials(p.name)}</span>
                    <span className="qb-person-name">
                      {p.name.slice(0, p.at)}
                      <b>{p.name.slice(p.at, p.at + lo)}</b>
                      {p.name.slice(p.at + lo)}
                    </span>
                    <span className="qb-person-sub">
                      {p.videos === 1 ? t("In 1 video") : t("In {{count}} videos", { count: p.videos })}
                    </span>
                  </div>
                ))}
              </div>
              {!items.length && (
                <div className="qb-people-none">
                  <span>{t("No one matching “{{q}}” in {{folder}}", { q: at.q, folder: folderName })}</span>
                  {folder !== ALL_FOLDERS && (
                    <button type="button" className="qb-link" onClick={() => onFolder(ALL_FOLDERS)}>
                      {t("Search all folders")}
                    </button>
                  )}
                </div>
              )}
              <div className="qb-people-foot">{t("↑ ↓ to choose · Enter to insert · Esc to close")}</div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="qb-field qb-folder"
          title={t("Only search videos in this folder. @ suggestions come from it too.")}
          aria-haspopup="menu"
          aria-expanded={!!folderMenu}
          onClick={(e) => setFolderMenu(e.currentTarget)}
        >
          <span className="qb-label">{t("Search in")}</span>
          <span className="qb-folder-value">{folder === ALL_FOLDERS ? t("All folders") : folder}</span>
          <ExpandMoreIcon className="qb-folder-icon" />
        </button>
        <Menu
          anchorEl={folderMenu}
          open={!!folderMenu}
          onClose={() => setFolderMenu(null)}
          className="searchMenu"
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { style: { minWidth: folderMenu?.offsetWidth } } }}
        >
          {[ALL_FOLDERS, ...folders].map((f) => (
            <MenuItem
              key={f || "all"}
              selected={f === folder}
              onClick={() => {
                setFolderMenu(null);
                onFolder(f);
              }}
            >
              <FolderIcon />
              {f === ALL_FOLDERS ? t("All folders") : f}
              {f === folder && <CheckIcon className="vv-menuCheck" />}
            </MenuItem>
          ))}
        </Menu>
        <button type="button" className="qb-submit" onClick={onSubmit} disabled={busy || !text.trim()}>
          {t("Search")}
        </button>
      </div>
      <div className="qb-row qb-under">
        {estimate ? (
          <div className="qb-chips">
            <span className="qb-estimate">
              <TimerOutlinedIcon fontSize="inherit" />
              {t("Estimated search time ~{{seconds}} s", { seconds: estimate })}
            </span>
          </div>
        ) : concepts.length > 0 ? (
          <div className="qb-chips">
            <span className="qb-caption">{t("Understood as")}</span>
            {concepts.map((c, i) => {
              const [label, tip] = conceptLabel(t, c);
              return (
                <span key={i} className="qb-concept" title={tip}>
                  {label}
                </span>
              );
            })}
          </div>
        ) : (
          <div className="qb-chips" />
        )}
        <span className="qb-hint">
          {text.length >= MAX_QUERY - 50 ? (
            t("{{count}} characters left", { count: MAX_QUERY - text.length })
          ) : (
            <>
              {dirty ? <KeyboardReturnIcon fontSize="inherit" /> : <AlternateEmailIcon fontSize="inherit" />}
              {dirty ? t("Press Enter to search") : t("Type @ to mention someone from {{folder}}", { folder: folderName })}
            </>
          )}
        </span>
      </div>
    </div>
  );
}
